import { AddressFragment, OrderDeliveryType, OrderDetailsFragment } from "@dashboard/graphql";

import { isStorePickup } from "../../utils/delivery";

export type OrderPrintDocument = "shippingLabel" | "pickupTicket";

export interface OrderPrintPaper {
  id: string;
  // Page size handed to the browser's print dialog. A null height means a
  // continuous roll: the page is cut to the measured content height.
  pageWidthMm: number;
  pageHeightMm: number | null;
  // Width of the printed document itself (a label on A4 only uses the top half).
  contentWidthMm: number;
  contentHeightMm: number | null;
  // Cap for a content-height document (the A4 label never passes the halfway cut).
  maxContentHeightMm?: number;
  marginMm: number;
  // Text size relative to the A5 label / 80 mm ticket the layouts are drawn for.
  fontScale: number;
}

// "Half an A4, portrait" is A5. On a plain A4 sheet the label sits at the top and
// is only as tall as its content, so the cut line comes right after it.
const SHIPPING_LABEL_PAPERS: OrderPrintPaper[] = [
  {
    id: "a5",
    pageWidthMm: 148,
    pageHeightMm: 210,
    contentWidthMm: 148,
    contentHeightMm: 210,
    marginMm: 0,
    fontScale: 1,
  },
  {
    id: "a4-half",
    pageWidthMm: 210,
    pageHeightMm: 297,
    contentWidthMm: 210,
    contentHeightMm: null,
    maxContentHeightMm: 148.5,
    marginMm: 0,
    fontScale: 0.9,
  },
  {
    id: "label-100x150",
    pageWidthMm: 100,
    pageHeightMm: 150,
    contentWidthMm: 100,
    contentHeightMm: 150,
    marginMm: 0,
    fontScale: 0.75,
  },
];

const PICKUP_TICKET_PAPERS: OrderPrintPaper[] = [
  {
    id: "roll-80",
    pageWidthMm: 80,
    pageHeightMm: null,
    contentWidthMm: 80,
    contentHeightMm: null,
    marginMm: 0,
    fontScale: 1,
  },
  {
    id: "roll-58",
    pageWidthMm: 58,
    pageHeightMm: null,
    contentWidthMm: 58,
    contentHeightMm: null,
    marginMm: 0,
    fontScale: 0.78,
  },
  {
    id: "a4",
    pageWidthMm: 210,
    pageHeightMm: 297,
    contentWidthMm: 110,
    contentHeightMm: null,
    marginMm: 15,
    fontScale: 1.25,
  },
];

// Optional lines at the foot of the shipping label; staff pick which to print.
export interface ShippingLabelFields {
  orderNumber: boolean;
  shippingMethod: boolean;
  itemCount: boolean;
  printedAt: boolean;
}

export const DEFAULT_SHIPPING_LABEL_FIELDS: ShippingLabelFields = {
  orderNumber: true,
  shippingMethod: true,
  itemCount: true,
  printedAt: true,
};

export const getPapers = (document: OrderPrintDocument): OrderPrintPaper[] =>
  document === "pickupTicket" ? PICKUP_TICKET_PAPERS : SHIPPING_LABEL_PAPERS;

export const getPaper = (document: OrderPrintDocument, id: string | undefined): OrderPrintPaper => {
  const papers = getPapers(document);

  return papers.find(paper => paper.id === id) ?? papers[0];
};

type PrintableOrder = Pick<
  OrderDetailsFragment,
  "deliveryType" | "deliveryMethod" | "isShippingRequired" | "status"
>;

// In-store pickup orders get a pickup ticket; everything that travels by courier
// (home delivery or a GLS pickup point) gets a shipping label.
export const getOrderPrintDocument = (
  order: PrintableOrder | undefined,
): OrderPrintDocument | null => {
  if (!order) {
    return null;
  }

  if (order.deliveryMethod?.__typename === "Warehouse" || isStorePickup(order.deliveryType)) {
    return "pickupTicket";
  }

  if (order.isShippingRequired || order.deliveryType === OrderDeliveryType.PARCEL_SHOP) {
    return "shippingLabel";
  }

  return null;
};

const MM_PER_CSS_PX = 25.4 / 96;

export const pxToMm = (px: number) => Math.ceil(px * MM_PER_CSS_PX);

export const getPageSizeRule = (paper: OrderPrintPaper, measuredHeightMm: number) =>
  `@page { size: ${paper.pageWidthMm}mm ${
    paper.pageHeightMm ?? measuredHeightMm + 2 * paper.marginMm
  }mm; margin: ${paper.marginMm}mm; }`;

// Some integrations persist the literal "null"/"undefined" for optional fields.
const clean = (value: string | null | undefined) => {
  const trimmed = (value ?? "").trim();

  return ["null", "undefined"].includes(trimmed.toLowerCase()) ? "" : trimmed;
};

export const getAddressName = (address: AddressFragment | null | undefined) =>
  address ? [clean(address.firstName), clean(address.lastName)].filter(Boolean).join(" ") : "";

interface AddressLineOptions {
  // Omit the person's name (shown separately as the recipient).
  withName?: boolean;
  // Country of the sender: a domestic label doesn't need the country line.
  homeCountryCode?: string;
}

export const getAddressLines = (
  address: AddressFragment | null | undefined,
  { withName = true, homeCountryCode }: AddressLineOptions = {},
): string[] => {
  if (!address) {
    return [];
  }

  const postalCity = [clean(address.postalCode), clean(address.city)].filter(Boolean).join(" ");
  const showCountry = !homeCountryCode || address.country?.code !== homeCountryCode;

  return [
    withName ? getAddressName(address) : "",
    clean(address.companyName),
    clean(address.streetAddress1),
    clean(address.streetAddress2),
    [postalCity, clean(address.cityArea)].filter(Boolean).join(", "),
    clean(address.countryArea),
    showCountry ? clean(address.country?.country) : "",
  ].filter(Boolean);
};
